/**
 * Message-scanning helpers extracted from `deriveRemixPhase`.
 *
 * Loom feedback #6: "all these utils can probably live in another file just to
 * make understanding [the derivation] one clearer." These are the low-level,
 * message-array-scanning predicates; keeping them separate leaves the phase
 * derivation itself readable as a small, high-level function.
 *
 * These helpers are React-free and pure.
 */

import { isAIFCAction } from '@atlassian/conversation-assistant-action-helpers/utils';
import {
	AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	DYNAMIC_UI_CREATE_KEY,
	isImageCreateData,
	isRemixGeneratedFlow,
	LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import type { StoreChatItem } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import {
	isStoreAssistant,
	isStoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

/**
 * Whiteboard (diagramming) action keys.
 */
export const isWhiteboardKey = (key: string): boolean =>
	key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY || key === CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY;

/**
 * Finds the most recent action in the CURRENT turn matching `predicate`.
 *
 * Why not `findLastAssistantAction`: that helper only inspects ASSISTANT
 * messages (`content.message.message.actions`), which are a *mirrored copy* of
 * the action produced when the assistant's FINAL_RESPONSE lands. The
 * authoritative and **earliest** source is the `ACTION_PREPARE` /
 * `ACTION_COMPLETE` chat items themselves (`item.action`), which arrive before
 * the final response — in a real stream, `ACTION_COMPLETE` precedes
 * `FINAL_RESPONSE`. Reading only the mirror meant the result payload appeared to
 * be missing at the exact moment the flow became ready.
 *
 * This scans both sources, newest-first, and stops at the turn boundary (a HUMAN
 * message) so a previous turn's result is never mistaken for this turn's.
 */
const findLatestRemixAction = (
	messages: StoreChatItem[],
	predicate: (action: MessageAction) => boolean,
): MessageAction | undefined => {
	for (let i = messages.length - 1; i >= 0; i--) {
		const message = messages[i];

		if (
			(message.type === 'ACTION_COMPLETE' || message.type === 'ACTION_PREPARE') &&
			message.action &&
			predicate(message.action)
		) {
			return message.action;
		}

		if (isStoreAssistant(message)) {
			// `content` can be absent on a partially-streamed assistant message.
			const actions = message.content?.message?.message?.actions ?? [];
			for (let j = actions.length - 1; j >= 0; j--) {
				if (predicate(actions[j])) {
					return actions[j];
				}
			}
		}

		if (isStoreHuman(message)) {
			break;
		}
	}
	return undefined;
};

/**
 * The property required to render a remix preview, keyed by action type:
 *   - MAUI app / dynamic UI (`DYNAMIC_UI_CREATE_KEY`) → `data.id`
 *   - Whiteboard / diagram                            → `data.contentId`
 *   - Infographic / image                             → `data.mediaFileId`
 *
 * A remix is "ready" precisely when this property is present — regardless of
 * remix type. Whiteboards still go ready early, but not via a special case:
 * their `contentId` is simply produced early in the stream (the whiteboard then
 * streams its content in through a separate mechanism), so the *same* rule
 * ("do we have the property needed to show the preview?") marks it ready as soon
 * as `contentId` exists.
 *
 * Reads the action from the `ACTION_*` chat items as well as the assistant
 * mirror (see `findLatestRemixAction`), so the property is picked up as soon as
 * it exists — no waiting on the final response.
 *
 * Dynamic-ui actions are status-gated (an `id` exists while still planned but is
 * not a previewable result); whiteboards are intentionally NOT status-gated
 * (their `contentId` is the early-ready signal); image results are inherently
 * complete once `mediaFileId` is present.
 */
const isPreResultStatus = (status: string | undefined): boolean =>
	status === 'preparing' || status === 'streaming' || status === 'planned';

/** The action carrying this turn's previewable result, if any. */
const findResolvedResultAction = (messages: StoreChatItem[]): MessageAction | undefined =>
	findLatestRemixAction(messages, (candidate) => {
		const data = candidate.data as { id?: string; contentId?: string } | undefined;
		switch (candidate.key) {
			case DYNAMIC_UI_CREATE_KEY:
				// A dynamic-ui action carries an `id` even while still planned /
				// streaming — that id is NOT a previewable result yet. Only treat
				// it as ready once the action has moved past its pre-result status.
				return Boolean(data?.id) && !isPreResultStatus(candidate.status);
			case CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY:
			case CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY:
			case AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY:
			case LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY:
				// Whiteboards resolve as soon as `contentId` exists — the board then
				// streams its content in separately.
				return Boolean(data?.contentId);
			default:
				// Image / infographic: the media only exists once generated, so the
				// presence of `mediaFileId` is itself the completion signal.
				return isImageCreateData(candidate.data) && Boolean(candidate.data.mediaFileId);
		}
	});

const resultIdOf = (action: MessageAction): string | undefined => {
	const data = action.data as { id?: string; contentId?: string } | undefined;
	switch (action.key) {
		case DYNAMIC_UI_CREATE_KEY:
			return data?.id;
		case CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY:
		case CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY:
		case AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY:
		case LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY:
			return data?.contentId;
		default:
			return isImageCreateData(action.data) ? action.data.mediaFileId : undefined;
	}
};

export const getResolvedResultId = (messages: StoreChatItem[]): string | undefined => {
	const action = findResolvedResultAction(messages);
	return action ? resultIdOf(action) : undefined;
};

/**
 * The remix result payload for the current turn — the identifiers a consumer
 * needs to render or insert the generated artefact.
 *
 * This is the supported way to read a result. The model deliberately does NOT
 * store a copy of the result (conversation stays the source of truth), and it no
 * longer routes result lookup through a *message* id: the payload lives on the
 * action, which is available exactly when the flow becomes ready.
 */
export type RemixResultPayload = {
	/** The action key that produced the result (e.g. `image-create`). */
	key: string;
	/** Type-appropriate identifier: app id, whiteboard contentId, or mediaFileId. */
	resultId?: string;
	/**
	 * Unique id of the action *invocation* that produced this result.
	 *
	 * Distinct from `resultId`: a whiteboard update deliberately reuses the same
	 * `contentId`, so the result id is NOT unique per generation. The invocation
	 * id always is, which makes it the correct value to hand back as an edit's
	 * `updateSentinel`.
	 */
	invocationId: string;
	mediaFileId?: string;
	mediaUrl?: string;
	mediaCollection?: string;
	contentId?: string;
};

export const getRemixResultPayload = (
	messages: StoreChatItem[] | undefined,
): RemixResultPayload | undefined => {
	if (!messages) {
		return undefined;
	}
	const action = findResolvedResultAction(messages);
	if (!action) {
		return undefined;
	}
	const data = action.data as
		| { contentId?: string; mediaCollection?: string; mediaFileId?: string; mediaUrl?: string }
		| undefined;
	return {
		key: action.key,
		resultId: resultIdOf(action),
		invocationId: action.invocationId,
		mediaFileId: data?.mediaFileId,
		mediaUrl: data?.mediaUrl,
		mediaCollection: data?.mediaCollection,
		contentId: data?.contentId,
	};
};

/**
 * The invocation id of the action carrying this turn's result — the value an
 * edit should compare its `updateSentinel` against.
 *
 * Why not the result id: a whiteboard *update* reuses the existing board, so the
 * new result carries the SAME `contentId` as the one already on the page.
 * Comparing result ids would therefore suppress the new result forever and the
 * flow would hang in `thinking`. `invocationId` is unique per action invocation
 * and is present from `ACTION_PREPARE` onward, so it distinguishes "the result
 * already displayed" from "the result just generated" in every case.
 */
export const getResolvedResultInvocationId = (messages: StoreChatItem[]): string | undefined =>
	findResolvedResultAction(messages)?.invocationId;

/**
 * Whiteboard remix action keys (create/update + diagramming variants).
 */
const isWhiteboardRemixActionKey = (key: string): boolean =>
	key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY ||
	key === CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY ||
	key === AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY ||
	key === LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY;

/**
 * True once a whiteboard remix action has begun streaming its parts
 * (`status: 'streaming'`, driven by ACTION_PART frames).
 *
 * This is the whiteboard early-ready signal: the board is created up-front and
 * renders progressively as parts arrive, so the preview is opened as soon as
 * content starts flowing rather than waiting for the action to be sealed.
 * Mirrors production's `hasWhiteboardRemixActionStreaming`.
 */
const hasWhiteboardActionStreaming = (messages: StoreChatItem[]): boolean =>
	findLatestRemixAction(
		messages,
		(action) =>
			isWhiteboardRemixActionKey(action.key) && action.status?.toLowerCase() === 'streaming',
	) !== undefined;

/**
 * True when the latest turn contains an `ACTION_COMPLETE`. Scans newest-first and
 * stops at the turn boundary (a HUMAN message) so a completed action from an
 * earlier turn is not mistaken for this turn's result.
 */
const hasActionCompleteInLatestTurn = (messages: StoreChatItem[]): boolean => {
	for (let i = messages.length - 1; i >= 0; i--) {
		const message = messages[i];
		if (message.type === 'ACTION_COMPLETE') {
			return true;
		}
		if (isStoreHuman(message)) {
			break;
		}
	}
	return false;
};

/**
 * True when the latest turn's assistant mirror contains a planned AIFC action.
 *
 * Keep this assistant-only: `ACTION_PREPARE` may itself carry `status: 'planned'`,
 * but that is not a completed result. The Confluence renderer's legacy helper in
 * `confluence/next/packages/malleable-ui-common/src/useRemixToAIFC.ts` scans
 * assistant mirrors across the whole conversation; that is equivalent for renderer
 * creates because each flow gets a fresh conversation. The shared machine also
 * supports updates that reuse a conversation, so it intentionally stops at the
 * latest HUMAN boundary to avoid treating a previous turn's result as completion
 * of the current update. Shadow parity diagnostics should not classify that
 * update-only correction as a renderer regression.
 */
const hasPlannedAIFCActionInLatestTurn = (messages: StoreChatItem[]): boolean => {
	for (let i = messages.length - 1; i >= 0; i--) {
		const message = messages[i];
		if (isStoreHuman(message)) {
			return false;
		}
		if (isStoreAssistant(message)) {
			const actions = message.content?.message?.message?.actions ?? [];
			if (actions.some((action) => isAIFCAction(action) && action.status === 'planned')) {
				return true;
			}
		}
	}
	return false;
};

/**
 * Intentionally mirrors the completion predicate in
 * `confluence/next/packages/malleable-ui-common/src/useRemixToAIFC.ts`. Keep this
 * local for CCCEMAU-3478 so the shadow write does not change legacy or editor
 * behavior. Follow-up: consolidate it with that renderer copy and the editor copy
 * in `platform/packages/malleable-ui/malleable-ui-components/src/ui/editor-remix/utils.ts`
 * while preserving their different readiness timing contracts.
 */
const hasCompletedRemixAction = (messages: StoreChatItem[]): boolean => {
	return hasActionCompleteInLatestTurn(messages) || hasPlannedAIFCActionInLatestTurn(messages);
};

/**
 * True when the latest turn has a remix-generated `ACTION_PREPARE` but never an
 * `ACTION_COMPLETE`. Combined with an idle conversation this means the stream
 * finished without sealing the action (the AdfEditorMinion "no operations
 * returned" early-exit). Mirrors production's `hasActionPrepareWithoutComplete`.
 */
const hasActionPrepareWithoutComplete = (messages: StoreChatItem[]): boolean => {
	let sawRemixPrepare = false;
	for (let i = messages.length - 1; i >= 0; i--) {
		const message = messages[i];
		if (message.type === 'ACTION_COMPLETE') {
			return false;
		}
		if (message.type === 'ACTION_PREPARE') {
			sawRemixPrepare = Boolean(message.action) && isRemixGeneratedFlow(message.action!);
		}
		if (isStoreHuman(message)) {
			break;
		}
	}
	return sawRemixPrepare;
};

/**
 * True when the remix preview can be opened. This mirrors production's
 * `onActionReady` gate in `useRemixToAIFC`, which is deliberately type-dependent:
 *
 *   - **Whiteboards / diagrams** become ready as soon as the action starts
 *     streaming parts. The board already exists (its `contentId` arrives on
 *     `ACTION_PREPARE`) and fills in progressively, so waiting for completion
 *     would needlessly delay a preview that is designed to render as it streams.
 *   - **Every other remix type** (infographic, image, MAUI app) waits for the
 *     conversation to become idle with a completed action. Their result is a
 *     single sealed payload — an announced but incomplete action has no
 *     previewable data.
 *   - **Fallback:** an idle conversation whose turn has a remix `ACTION_PREPARE`
 *     but no `ACTION_COMPLETE` (minion early-exit) is treated as ready so the
 *     flow does not hang in `thinking` forever.
 *
 * Note an `ACTION_PREPARE` alone is explicitly NOT readiness — that was the
 * source of previewing before any result data existed.
 */
export const isPreviewReady = (messages: StoreChatItem[], isIdle: boolean): boolean =>
	hasWhiteboardActionStreaming(messages) ||
	(isIdle && (hasCompletedRemixAction(messages) || hasActionPrepareWithoutComplete(messages)));

/**
 * True when the latest turn contains an assistant response but no completed
 * action and nothing still pending — i.e. the model exited early / the user
 * aborted.
 */
export const looksAborted = (messages: StoreChatItem[]): boolean => {
	let sawAssistant = false;
	let sawPrepare = false;
	for (let i = messages.length - 1; i >= 0; i--) {
		const message = messages[i];
		if (message.type === 'ACTION_PREPARE') {
			sawPrepare = true;
		}
		if (isStoreAssistant(message)) {
			sawAssistant = true;
		}
		if (isStoreHuman(message)) {
			break;
		}
	}
	return sawAssistant && !sawPrepare && !hasCompletedRemixAction(messages);
};

/** Reads the current request's Stop marker without borrowing an older turn's cancellation. */
export const hasExplicitAbortInLatestTurn = (messages: StoreChatItem[]): boolean => {
	for (let i = messages.length - 1; i >= 0; i--) {
		const message = messages[i];
		if (isStoreHuman(message)) {
			break;
		}
		if (isStoreAssistant(message) && message.isAborted === true) {
			return true;
		}
	}
	return false;
};
